/* ============================================================
   NOBLE THEME — theme.js
   ============================================================ */

(function () {
  'use strict';

  // ── Colour lookup for variant swatches ──────────────────────
  const SWATCH_COLORS = {
    'charcoal':           { bg: '#222222' },
    'saddle':             { bg: '#8B5A2B' },
    'olive':              { bg: '#4B5320' },
    'midnight black':     { bg: '#1A1A1A' },
    'titanium silver':    { bg: '#D8D8D8' },
    'charcoal & silver':  { gradient: 'linear-gradient(to right, #222222 50%, #D8D8D8 50%)' },
    'saddle & black':     { gradient: 'linear-gradient(to right, #8B5A2B 50%, #1A1A1A 50%)' },
    'blank tear-away':    { bg: '#FFFFFF', border: '1px solid rgba(20,20,20,0.2)' },
    'dotted thread bound':{ bg: '#EAE5DC' },
  };

  function applySwatchColor(el) {
    const name = (el.dataset.colorName || '').toLowerCase();
    const def = SWATCH_COLORS[name];
    if (!def) return;
    if (def.gradient) {
      el.style.backgroundImage = def.gradient;
    } else {
      el.style.backgroundColor = def.bg || '#ccc';
    }
    if (def.border) el.style.outline = def.border;
  }

  // ── Helpers ──────────────────────────────────────────────────
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }
  function scrollTo(id) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  // ── Shared RAF-throttled scroll dispatcher ───────────────────
  // Single scroll listener shared by all init functions — avoids
  // multiple listeners fighting for the same frame budget.
  const _scrollCbs = [];
  let _scrollTicking = false;
  function onScroll(cb) { _scrollCbs.push(cb); }
  window.addEventListener('scroll', () => {
    if (_scrollTicking) return;
    _scrollTicking = true;
    requestAnimationFrame(() => {
      _scrollCbs.forEach(fn => fn());
      _scrollTicking = false;
    });
  }, { passive: true });

  // ── Cached DOM refs (queried once, reused everywhere) ────────
  let _cartBadgeEl = null;
  function getCartBadge() {
    return _cartBadgeEl || (_cartBadgeEl = document.getElementById('cart-badge'));
  }

  // ============================================================
  // NAVIGATION
  // ============================================================
  function initNavigation() {
    $$('[data-scroll-to]').forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.scrollTo;
        if (target === 'section-hero') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          scrollTo(target);
        }
      });
    });
  }

  // ============================================================
  // SMOOTH SCROLL (Lenis) — only if loaded + motion allowed
  // ============================================================
  function initSmoothScroll() {
    if (!window.Lenis) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var cfg = window.__nobleScroll || {};
    const lenis = new Lenis({
      duration: cfg.duration || 1.2,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: cfg.wheel || 1,
      touchMultiplier: 2,
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    window.__lenis = lenis;
  }

  // ============================================================
  // PREDICTIVE SEARCH — Shopify /search/suggest.json
  // ============================================================
  function initPredictiveSearch() {
    const openBtn = document.getElementById('search-open-btn');
    const overlay = document.getElementById('psearch');
    if (!openBtn || !overlay) return;
    const input = document.getElementById('psearch-input');
    const results = document.getElementById('psearch-results');
    const money = window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD';

    function setOpen(open) {
      overlay.classList.toggle('is-open', open);
      overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) { setTimeout(() => input && input.focus(), 50); }
    }
    openBtn.addEventListener('click', () => setOpen(true));
    overlay.querySelectorAll('[data-psearch-close]').forEach(el => el.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') setOpen(false); });

    if (results) results.innerHTML = '<p class="psearch__hint">Start typing to search…</p>';

    let timer;
    function render(q) {
      const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
      fetch(url)
        .then(r => r.json())
        .then(data => {
          const products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (!products.length) { results.innerHTML = '<p class="psearch__none">No matches for “' + q + '”.</p>'; return; }
          let html = '<div class="psearch__grid">';
          products.forEach(p => {
            const img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
            html += '<a class="pcard" href="' + p.url + '">' +
              '<div class="pcard__media">' + (img ? '<img class="pcard__img pcard__img--main" src="' + img + '" alt="" loading="lazy">' : '') + '</div>' +
              '<h3 class="pcard__title">' + p.title + '</h3>' +
              '<div class="pcard__price">' + (p.price ? p.price : '') + '</div>' +
              '</a>';
          });
          html += '</div><a class="psearch__viewall" href="/search?q=' + encodeURIComponent(q) + '">View all results →</a>';
          results.innerHTML = html;
        })
        .catch(() => { results.innerHTML = '<p class="psearch__none">Search is unavailable right now.</p>'; });
    }

    if (input) {
      input.addEventListener('input', () => {
        const q = input.value.trim();
        clearTimeout(timer);
        if (q.length < 2) { results.innerHTML = '<p class="psearch__hint">Start typing to search…</p>'; return; }
        timer = setTimeout(() => render(q), 250);
      });
    }
  }

  // ============================================================
  // SITE HEADER — transparent→solid on scroll + mobile menu
  // ============================================================
  function initHeader() {
    const header = document.getElementById('site-header');
    if (!header) return;

    // Turn the header solid once scrolled (and keep it solid on inner pages,
    // where the Liquid already adds .is-solid).
    if (!header.classList.contains('is-solid')) {
      const onScrollSolid = () => header.classList.toggle('is-solid', window.scrollY > 20);
      onScroll(onScrollSolid);
      onScrollSolid();
    }

    // Mobile menu toggle
    const toggle = document.getElementById('mobile-menu-toggle');
    const menu = document.getElementById('mobile-menu');
    if (toggle && menu) {
      const iconMenu = toggle.querySelector('.icon-menu');
      const iconClose = toggle.querySelector('.icon-close');
      const setOpen = (open) => {
        menu.classList.toggle('is-open', open);
        menu.setAttribute('aria-hidden', open ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        if (iconMenu) iconMenu.style.display = open ? 'none' : '';
        if (iconClose) iconClose.style.display = open ? '' : 'none';
        document.body.style.overflow = open ? 'hidden' : '';
      };
      toggle.addEventListener('click', () => setOpen(!menu.classList.contains('is-open')));
      menu.querySelectorAll('a').forEach(a => a.addEventListener('click', () => setOpen(false)));
      document.addEventListener('keydown', e => { if (e.key === 'Escape') setOpen(false); });
    }
  }

  // ============================================================
  // HERO — scroll text animation + video mute
  // ============================================================
  function initHero() {
    const section = document.getElementById('section-hero');
    if (!section) return;

    // Video mute toggle
    const video = section.querySelector('.hero-video');
    const isVideoEl = video && video.tagName === 'VIDEO';
    const muteBtn = section.querySelector('.hero-mute-btn');
    const muteIcon = section.querySelector('.hero-mute-icon');
    const unmuteIcon = section.querySelector('.hero-unmute-icon');

    // Force autoplay on mobile (esp. iOS): muted must be set as a property,
    // and .play() must be called. If the browser still blocks it, resume on
    // the first user interaction so the play button never gets stuck.
    if (isVideoEl) {
      video.muted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;
      const tryPlay = () => { const p = video.play(); if (p && p.catch) p.catch(() => {}); };
      tryPlay();
      const resume = () => { tryPlay(); document.removeEventListener('touchstart', resume); document.removeEventListener('click', resume); };
      document.addEventListener('touchstart', resume, { passive: true, once: true });
      document.addEventListener('click', resume, { once: true });
    }

    if (isVideoEl && muteBtn) {
      muteBtn.addEventListener('click', () => {
        video.muted = !video.muted;
        video.volume = video.muted ? 0 : 1;
        if (video.muted === false) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
        if (muteIcon)   muteIcon.style.display   = video.muted ? '' : 'none';
        if (unmuteIcon) unmuteIcon.style.display = video.muted ? 'none' : '';
      });
    }

    // --- Scroll-driven sequence over the 400vh hero ---
    const center = section.querySelector('[data-hero-center]');
    const b1 = section.querySelector('[data-hero-b1]');
    const b2 = section.querySelector('[data-hero-b2]');
    const win = section.querySelector('[data-hero-window]');
    const p1 = section.querySelector('[data-hero-p1]');
    const p2 = section.querySelector('[data-hero-p2]');
    const p3 = section.querySelector('[data-hero-p3]');

    const clamp01 = x => Math.max(0, Math.min(1, x));
    const map = (p, a, b) => clamp01((p - a) / (b - a));

    const hasWindow = !!win;

    function update() {
      const rect = section.getBoundingClientRect();
      const range = section.offsetHeight - window.innerHeight;
      const p = range > 0 ? clamp01(-rect.top / range) : 0;
      const vh = window.innerHeight;

      // No rising window → short hero: spread the two text blocks across the
      // whole scroll and leave block 2 visible at the end (no empty scroll).
      if (!hasWindow) {
        if (b1) {
          const t = map(p, 0, 0.30);
          b1.style.opacity = 1 - t;
          b1.style.transform = 'translateY(' + (-40 * t) + 'px)';
          b1.style.filter = 'blur(' + (10 * t) + 'px)';
        }
        if (b2) {
          const inP = map(p, 0.45, 0.80);
          b2.style.opacity = inP;
          b2.style.transform = 'translateX(' + (-40 * (1 - inP)) + 'px)';
          b2.style.filter = 'blur(' + (10 * (1 - inP)) + 'px)';
          b2.style.pointerEvents = inP > 0.5 ? 'auto' : 'none';
        }
        if (center) center.style.opacity = 1;
        return;
      }

      // Block 1: fade/slide out 0 → 10%
      if (b1) {
        const t = map(p, 0, 0.10);
        b1.style.opacity = 1 - t;
        b1.style.transform = 'translateY(' + (-40 * t) + 'px)';
        b1.style.filter = 'blur(' + (10 * t) + 'px)';
      }
      // Block 2: in 15→25%, hold, out 30→35%
      if (b2) {
        const inP = map(p, 0.15, 0.25);
        const outP = map(p, 0.30, 0.35);
        const op = inP * (1 - outP);
        b2.style.opacity = op;
        b2.style.transform = 'translateX(' + (-40 * (1 - inP)) + 'px)';
        b2.style.filter = 'blur(' + (10 * (1 - inP) + 10 * outP) + 'px)';
        b2.style.pointerEvents = op > 0.5 ? 'auto' : 'none';
      }
      // Centre title eases back as the window rises 35→50%
      if (center) center.style.opacity = 1 - map(p, 0.35, 0.5) * 0.85;
      // Window rises 35→65%, fades in 35→55%
      if (win) {
        const rise = 1 - map(p, 0.35, 0.65);
        win.style.transform = 'translateY(' + (rise * 0.8 * vh) + 'px)';
        win.style.opacity = map(p, 0.35, 0.55);
      }
      // Paragraphs fade/rise in sequence
      const para = (el, a, b) => {
        if (!el) return;
        const t = map(p, a, b);
        el.style.opacity = t;
        el.style.transform = 'translateY(' + (30 * (1 - t)) + 'px)';
      };
      para(p1, 0.55, 0.68);
      para(p2, 0.68, 0.81);
      para(p3, 0.81, 0.94);
    }

    onScroll(update);
    window.addEventListener('resize', update);
    update();
  }
  // ============================================================
  // PRODUCTS GRID (Section 3)
  // ============================================================
  function initProductsGrid() {
    const section = document.getElementById('products-section');
    if (!section) return;

    // Apply swatch colours
    $$('.product-swatch', section).forEach(applySwatchColor);

    const cards = $$('.product-card', section);

    // Track selected variant per card
    const selectedVariants = {};

    cards.forEach((card, cardIdx) => {
      const cardId = card.dataset.cardId || cardIdx;
      const swatches = $$('.product-swatch', card);
      const addBtn = card.querySelector('.add-to-cart-btn');
      const qtyControl = card.querySelector('.qty-control');
      const priceEl = card.querySelector('.product-card__price');

      // Set first swatch as selected
      if (swatches.length) {
        swatches[0].classList.add('is-selected');
        selectedVariants[cardId] = {
          id: swatches[0].dataset.variantId,
          price: swatches[0].dataset.price,
        };
      }

      // Swatch click
      swatches.forEach(swatch => {
        swatch.addEventListener('click', e => {
          e.stopPropagation();
          swatches.forEach(s => s.classList.remove('is-selected'));
          swatch.classList.add('is-selected');
          selectedVariants[cardId] = {
            id: swatch.dataset.variantId,
            price: swatch.dataset.price,
          };
          if (priceEl && swatch.dataset.price) {
            priceEl.textContent = swatch.dataset.price;
          }
          // Refresh qty display for new variant
          refreshQtyDisplay(card, cardId, qtyControl, addBtn);
        });
      });

      // Hover expands the card (desktop). Click navigates to the product
      // page — except when the click lands on an interactive control
      // (swatch, add-to-cart, qty stepper).
      card.addEventListener('mouseenter', () => expandCard(card, cards));
      card.addEventListener('click', e => {
        if (e.target.closest('button, a, .product-swatch, .qty-control')) return;
        const url = card.dataset.productUrl;
        if (url) {
          window.location.href = url;
        } else {
          expandCard(card, cards);
        }
      });

      // Add to cart
      if (addBtn) {
        addBtn.addEventListener('click', e => {
          e.stopPropagation();
          const variant = selectedVariants[cardId];
          if (variant && variant.id) {
            cartAdd(variant.id).then(() => refreshQtyDisplay(card, cardId, qtyControl, addBtn));
          }
        });
      }
    });

    function expandCard(card, all) {
      all.forEach(c => c.classList.toggle('is-expanded', c === card));
    }

    function refreshQtyDisplay(card, cardId, qtyControl, addBtn) {
      const variantId = (selectedVariants[cardId] || {}).id;
      if (!variantId) return;

      cartGet().then(cart => {
        const item = cart.items.find(i => String(i.variant_id) === String(variantId));
        const qty = item ? item.quantity : 0;

        if (qty === 0) {
          if (addBtn) addBtn.style.display = '';
          if (qtyControl) qtyControl.style.display = 'none';
        } else {
          if (addBtn) addBtn.style.display = 'none';
          if (qtyControl) {
            qtyControl.style.display = 'flex';
            const numEl = qtyControl.querySelector('.qty-num');
            if (numEl) numEl.textContent = qty;
          }
        }
      });
    }

    // Hook up qty-control buttons (delegated)
    section.addEventListener('click', e => {
      const btn = e.target.closest('.qty-control .qty-btn');
      if (!btn) return;
      e.stopPropagation();
      const control = btn.closest('.qty-control');
      const card = btn.closest('.product-card');
      if (!card) return;
      const cardId = card.dataset.cardId || cards.indexOf(card);
      const variantId = (selectedVariants[cardId] || {}).id;
      if (!variantId) return;
      const numEl = control.querySelector('.qty-num');
      const currentQty = parseInt(numEl ? numEl.textContent : '1', 10);
      const delta = btn.dataset.action === 'remove' ? -1 : 1;
      const newQty = Math.max(0, currentQty + delta);

      cartGetLineKey(variantId).then(key => {
        if (key) cartChange(key, newQty).then(() => {
          const addBtn = card.querySelector('.add-to-cart-btn');
          const qtyControl = card.querySelector('.qty-control');
          refreshQtyDisplay(card, cardId, qtyControl, addBtn);
        });
      });
    });
  }
  // ============================================================
  // STORIES (Section 6)
  // ============================================================
  function initStories() {
    const section = document.querySelector('.stories-section');
    if (!section) return;

    const slides = $$('.story-slide', section);
    const dots = $$('.stories-dot', section);
    const textEl = section.querySelector('.stories-text');
    let current = 0;
    const INTERVAL = 5000;

    function showStory(idx) {
      slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      if (textEl && slides[idx]) {
        textEl.textContent = slides[idx].dataset.text || '';
      }
      current = idx;
    }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        showStory(i);
        clearInterval(timer);
        timer = setInterval(() => showStory((current + 1) % slides.length), INTERVAL);
      });
    });

    let timer = setInterval(() => showStory((current + 1) % slides.length), INTERVAL);
    if (slides.length) showStory(0);
  }
  // ============================================================
  // AJAX CART
  // ============================================================
  // Re-render the cart drawer from Shopify's Section Rendering API response.
  function applyCartSections(sections) {
    if (!sections) return;
    const html = sections['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-body]');
    const current = document.querySelector('#cart-drawer [data-cart-body]');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
    const root = doc.querySelector('#cart-drawer');
    const count = root ? root.getAttribute('data-cart-count') : null;
    if (count != null) {
      const badge = getCartBadge();
      if (badge) { badge.textContent = count; badge.style.display = Number(count) > 0 ? 'flex' : 'none'; }
    }
  }

  async function cartAdd(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity, sections: 'cart-drawer', sections_url: window.location.pathname }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.error('Cart add error:', err.description || res.status);
      return null;
    }
    const data = await res.json();
    if (data.sections) { applyCartSections(data.sections); } else { await refreshCartUI(); }
    openCart();
    return data;
  }

  async function cartChange(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity, sections: 'cart-drawer', sections_url: window.location.pathname }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.sections) { applyCartSections(data.sections); } else { await refreshCartUI(); }
    return data;
  }

  async function cartGet() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  async function refreshCartUI() {
    // Drawer is rendered server-side; pull the latest section markup on demand.
    try {
      const res = await fetch(window.location.pathname + '?sections=cart-drawer', { headers: { 'Accept': 'application/json' } });
      const data = await res.json();
      applyCartSections(data);
    } catch (e) {}
  }

  async function cartGetLineKey(variantId) {
    const cart = await cartGet();
    const item = cart.items.find(i => String(i.variant_id) === String(variantId));
    return item ? item.key : null;
  }

  function updateCartBadge(cart) {
    const badge = getCartBadge();
    if (!badge) return;
    const count = cart.item_count || 0;
    badge.textContent = count;
    badge.style.display = count > 0 ? 'flex' : 'none';
    updateFreeShipping(cart);
  }

  function formatMoney(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }

  function updateFreeShipping(cart) {
    const wrap = document.getElementById('freeship');
    if (!wrap) return;
    const threshold = parseInt(wrap.dataset.threshold, 10) || 0;
    if (threshold <= 0) { wrap.style.display = 'none'; return; }
    const total = cart.total_price || 0;
    const msg = document.getElementById('freeship-msg');
    const bar = document.getElementById('freeship-bar');
    const pct = Math.min(100, (total / threshold) * 100);
    if (bar) bar.style.width = pct + '%';
    if (msg) {
      if (total >= threshold) { msg.innerHTML = '🎉 You’ve unlocked <strong>free shipping</strong>!'; }
      else { msg.innerHTML = 'You’re ' + formatMoney(threshold - total) + ' away from <strong>free shipping</strong>.'; }
    }
  }

  function renderCart(cart) {
    const itemsEl = document.getElementById('cart-items');
    const totalEl = document.getElementById('cart-total');
    if (!itemsEl) return;

    if (totalEl) {
      totalEl.textContent = (cart.total_price / 100).toLocaleString('en-US', {
        style: 'currency', currency: cart.currency || 'USD',
      });
    }

    if (!cart.items || cart.items.length === 0) {
      itemsEl.innerHTML = '<p class="cart-empty">Your cart is empty</p>';
      return;
    }

    itemsEl.innerHTML = cart.items.map(item => {
      const imgSrc = item.featured_image && item.featured_image.url
        ? item.featured_image.url + '&width=128'
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? item.variant_title : '';
      const linePrice = (item.final_line_price / 100).toLocaleString('en-US', {
        style: 'currency', currency: cart.currency || 'USD',
      });
      return `
        <div class="cart-item">
          ${imgSrc
            ? `<img class="cart-item__img" src="${imgSrc}" alt="${item.product_title}" loading="lazy">`
            : `<div class="cart-item__img"></div>`
          }
          <div class="cart-item__info">
            <div class="cart-item__name">${item.product_title}</div>
            ${variant ? `<div class="cart-item__variant">${variant}</div>` : ''}
            ${item.description ? `<div class="cart-item__desc">${item.description}</div>` : ''}
            <div class="cart-item__price">${linePrice}</div>
            <div class="cart-item__qty">
              <button class="qty-btn" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Remove one">−</button>
              <span class="qty-num">${item.quantity}</span>
              <button class="qty-btn" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Add one">+</button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Delegate qty clicks inside cart drawer
    $$('.cart-item__qty .qty-btn', itemsEl).forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const qty = Math.max(0, parseInt(btn.dataset.qty, 10));
        cartChange(key, qty);
      });
    });
  }

  function openCart() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
    }
  }

  function closeCart() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
  }

  function initCart() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    // Close on overlay click, the close button, or Escape.
    drawer.addEventListener('click', e => {
      if (e.target.closest('#cart-overlay') || e.target.closest('#cart-close')) closeCart();
    });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });

    // Quantity +/- and remove — delegated, so it survives section re-renders.
    drawer.addEventListener('click', e => {
      const btn = e.target.closest('.qty-btn');
      if (!btn) return;
      const line = btn.dataset.line;
      const qty = Math.max(0, parseInt(btn.dataset.qty, 10));
      if (line) cartChange(line, qty);
    });

    // Order note — debounced save (delegated; survives re-renders).
    let noteTimer;
    drawer.addEventListener('input', e => {
      if (e.target.id !== 'cart-note-field') return;
      const val = e.target.value;
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => {
        fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: val })
        }).catch(() => {});
      }, 600);
    });
  }

  // ============================================================
  // HORIZONTAL SCROLLERS — progress thumb (carousel / featured collection)
  // ============================================================
  function initHScrollers() {
    $$('[data-hscroll]').forEach(scroller => {
      const scope = scroller.closest('section') || document;
      const bar = scope.querySelector('[data-hscroll-bar]');
      if (!bar) return;
      const track = bar.parentElement;
      function update() {
        const sw = scroller.scrollWidth;
        const cw = scroller.clientWidth;
        if (sw <= cw + 1) { if (track) track.style.opacity = '0'; return; }
        if (track) track.style.opacity = '1';
        bar.style.width = (cw / sw * 100) + '%';
        bar.style.left = (scroller.scrollLeft / sw * 100) + '%';
      }
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  // ============================================================
  // PINNED VIDEO BACKGROUND — fade/pause after the last overlay section
  // ============================================================
  function initPinnedBg() {
    const bg = document.querySelector('[data-pinned-bg]');
    if (!bg) return;
    const video = bg.querySelector('video');
    const overlays = $$('[data-overlay-bg]');

    function zoneEnd() {
      if (overlays.length) {
        const last = overlays[overlays.length - 1];
        const r = last.getBoundingClientRect();
        return window.scrollY + r.bottom; // absolute Y of the last overlay's bottom
      }
      const hero = document.getElementById('section-hero');
      return hero ? hero.offsetTop + hero.offsetHeight : window.innerHeight;
    }

    function update() {
      const past = window.scrollY > zoneEnd();
      bg.style.opacity = past ? '0' : '1';
      if (video) {
        if (past) { video.pause(); }
        else { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
      }
    }

    onScroll(update);
    window.addEventListener('resize', update);
    update();
  }

  // ============================================================
  // SHARE BUTTONS (Web Share API + clipboard fallback)
  // ============================================================
  function initShareButtons() {
    $$('[data-share-url]').forEach(btn => {
      btn.addEventListener('click', () => {
        const url = btn.dataset.shareUrl;
        const title = btn.dataset.shareTitle || document.title;
        const fb = btn.querySelector('.product-share__feedback');
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(() => {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => {
            if (fb) { fb.textContent = 'Link copied'; setTimeout(() => { fb.textContent = ''; }, 2000); }
          });
        }
      });
    });
  }

  // ============================================================
  // PRODUCT RECOMMENDATIONS (Shopify Recommendations API)
  // ============================================================
  function initRecommendations() {
    $$('[data-recs]').forEach(el => {
      if (el.dataset.performed === 'true') return; // already server-rendered
      const pid = el.dataset.productId;
      const sid = el.dataset.sectionId;
      const limit = el.dataset.limit || 4;
      const intent = el.dataset.intent || 'related';
      if (!pid || !sid) return;
      const url = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') +
        'recommendations/products?section_id=' + sid + '&product_id=' + pid + '&limit=' + limit + '&intent=' + intent;
      fetch(url.replace('//recommendations', '/recommendations'))
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('[data-recs]');
          if (fresh && fresh.querySelector('.pcard')) { el.innerHTML = fresh.innerHTML; }
        })
        .catch(() => {});
    });
  }

  // ============================================================
  // RECENTLY VIEWED (localStorage)
  // ============================================================
  const RV_KEY = 'noble_recently_viewed';
  function rvGet() { try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; } }
  function initRecentlyViewedTracker() {
    const el = document.getElementById('rv-current');
    if (!el) return;
    let data;
    try { data = JSON.parse(el.textContent); } catch (e) { return; }
    if (!data || !data.handle) return;
    let list = rvGet().filter(i => i.handle !== data.handle);
    list.unshift(data);
    list = list.slice(0, 8);
    try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function initRecentlyViewed() {
    const section = document.querySelector('[data-recently-viewed]');
    if (!section) return;
    const grid = section.querySelector('[data-rv-grid]');
    const currentHandle = section.dataset.currentHandle || '';
    const items = rvGet().filter(i => i.handle !== currentHandle).slice(0, 4);
    if (!items.length || !grid) { section.style.display = 'none'; return; }
    grid.innerHTML = items.map(i =>
      '<a class="pcard" href="' + i.url + '">' +
      '<div class="pcard__media">' + (i.image ? '<img class="pcard__img pcard__img--main" src="' + i.image + '" alt="" loading="lazy">' : '') + '</div>' +
      '<h3 class="pcard__title">' + i.title + '</h3>' +
      '<div class="pcard__price">' + (i.price || '') + '</div></a>'
    ).join('');
    const h = section.querySelector('.recs__heading'); if (h) h.removeAttribute('hidden');
  }

  // ============================================================
  // SCROLL REVEAL — fade/rise [data-reveal] elements into view
  // ============================================================
  function initReveal() {
    const els = $$('[data-reveal]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(el => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el, i) => { el.style.transitionDelay = (i % 4 * 0.08) + 's'; io.observe(el); });
  }

  // ============================================================
  // BACK TO TOP
  // ============================================================
  function initBackToTop() {
    const btn = document.getElementById('back-to-top');
    if (!btn) return;
    onScroll(() => {
      const visible = window.scrollY > 300;
      btn.classList.toggle('is-visible', visible);
      // Let other corner controls (e.g. hero mute) shift up when the arrow shows.
      document.body.classList.toggle('show-backtotop', visible);
    });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  // Expose cart API to window so inline scripts in section templates
  // (e.g. main-product.liquid) can call window.cartAdd / window.cartChange
  window.cartAdd    = cartAdd;
  window.cartChange = cartChange;

  // ============================================================
  // BOOT
  // ============================================================
  document.addEventListener('DOMContentLoaded', () => {
    // Only the modules this theme actually uses. The Asphalt sections carry
    // their own inline behaviour; the old-theme inits (hero/products-grid/
    // stories/recs/recently-viewed/share/pinned-bg/hscroll/legacy header) are
    // intentionally not booted.
    initSmoothScroll();
    initNavigation();
    initPredictiveSearch();
    initCart();
    initBackToTop();
  });

})();
